import useCompanyId from '@/hooks/useCompanyId';
import { action } from '@/lib/api.js';
import getCompanyIdForRequest from '@/lib/getCompanyIdForRequest.js';
import setPath from '@/lib/setPath';
import removePref from '@/redux/actions/uiActions/removePref';
import setPref from '@/redux/actions/uiActions/setPref';
import { getUserState, putUserCompany } from '@/redux/actions/userActions';
import clearCredentials from '@/screens/jobs/lib/clearCredentials';
import clearFilters from '@/screens/jobs/lib/clearFilters';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { useSelector } from 'react-redux';
import { ORG_ACCESS_RESTRICTED_CODE } from 'shared/schema/staffAccess.js';

// Only the latest switch may move the tab; an older one that answers late is dropped.
let latestSwitch = 0;

const useChangeCompany = () => {
  const { user: clerkUser } = useClerkUser();
  const { setSessionCompanyId } = useCompanyId();
  const userId = useSelector((state) => state?.user?.jwt?.id);

  /* The tab moves to the company only once the server has answered the switch: a restricted organization can refuse
     a MindCloud staff member, and the request modal (opened by the API layer) takes over. Any other failure moves
     the tab as before. `callback` runs either way and receives the refusal, if any. */
  return (id, opts = { message: 'Please wait while we change companies...', isSignUp: false }, callback) => {
    const switchId = ++latestSwitch;
    if (!opts?.isSignUp) {
      setPref('platformLoading', { isLoading: true, message: opts.message, showBeforeUnloadWarning: false });
    }
    const stopLoading = () => {
      if (!opts?.isSignUp) {
        setTimeout(() => {
          setPref('platformLoading', { isLoading: false, showSpinner: false });
        }, 1000);
      }
    };

    putUserCompany({ userId, companyId: id }, async (_data, error) => {
      if (switchId !== latestSwitch) {
        return;
      }

      if (error?.code === ORG_ACCESS_RESTRICTED_CODE) {
        stopLoading();
        await callback?.(error);
        return;
      }

      // If redirect is true, redirect to either /workflows, /jobs, or /full-service.
      // This prevents stale deep links and clears query params like `home` for Full Service.
      if (opts?.redirect === true) {
        const isInWorkflowScreen = window.location.pathname.includes('/workflows');
        const isInJobScreen = window.location.pathname.includes('/jobs');
        const isInFullServiceScreen = window.location.pathname.includes('/full-service');

        if (isInFullServiceScreen) {
          setPath('/full-service');
        } else {
          const isInRedirectScreen = isInJobScreen || isInWorkflowScreen;
          const newPath = isInWorkflowScreen ? '/workflows' : '/jobs';
          if (isInRedirectScreen && newPath !== window.location.pathname) {
            setPath(newPath);
          }
        }
      }

      setSessionCompanyId(id);
      clearFilters();
      clearCredentials();
      // Clear locally now, but only persist the deletion after the selected
      // company's user state supplies its workspace authority.
      action('uiRemovePref', { key: 'copied-steps' });
      getUserState({ userId }, (data, stateError) => {
        if (stateError || !data?.workspaceRegion || data?.authorization?.userId !== userId || data?.authorization?.companyId !== id || getCompanyIdForRequest() !== id) {
          return;
        }
        removePref('copied-steps');
      });

      // Every switch waits here, so a failed Clerk reload or callback must not leave the loader up.
      try {
        await clerkUser.reload();
        await callback?.();
      } finally {
        stopLoading();
      }
    });
  };
};

export default useChangeCompany;
